/** Calculadora de prazos: manual (na hora) ou automática (feriados do sistema). Botão flutuante em Atividades, Casos e Processos. */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { match, useLocation } from "@/lib/router";
import { calcDeadline, weekday, type CalcInput, type CalcResult } from "@/lib/deadline-calc";
import { Field, FormGrid, Modal } from "./kit";

interface Calendar { organName: string | null; settings: { internoDiasAntes: number; recesso: boolean }; days: Array<{ date: string; name: string; kind: string }>; organs: string[] }

export interface CalcApply { fatalDueAt: string; internalDueAt: string | null; note: string }

/**
 * Atividade sendo criada agora (janela aberta): a calculadora preenche as datas DELA,
 * em vez de abrir uma atividade nova. A janela registra aqui enquanto está aberta.
 */
type Filler = { fill: (r: CalcApply) => void; processId: string | null };
const fillers: Filler[] = [];
export function useActivityDateFill(f: Filler): void {
  useEffect(() => {
    fillers.push(f);
    return () => { const i = fillers.indexOf(f); if (i >= 0) fillers.splice(i, 1); };
  }, [f]);
}
const currentFiller = (): Filler | null => fillers[fillers.length - 1] ?? null;

export function DeadlineCalculator({ processId, organName, onApply, applyLabel = "Usar este prazo" }: {
  processId?: string | null; organName?: string | null; onApply?: (r: CalcApply) => void; applyLabel?: string;
}): JSX.Element {
  const [mode, setMode] = useState<"manual" | "auto">(processId || organName ? "auto" : "manual");
  const [f, setF] = useState({ expedicao: "", disponibilizacao: "", inicio: "", dias: "15", tipo: "uteis" as "uteis" | "corridos", dobro: false, feriados: "0", interno: "", orgao: organName ?? "" });
  const up = (p: Partial<typeof f>) => setF({ ...f, ...p });
  const base = f.disponibilizacao || f.expedicao || f.inicio;
  const cal = useQuery({
    queryKey: ["prazos-feriados", base, f.orgao, processId ?? ""],
    queryFn: () => api.get<Calendar>(`/prazos/feriados${qs({ de: base ? addIso(base, -5) : undefined, orgao: f.orgao || undefined, processo: processId ?? undefined })}`),
    enabled: mode === "auto",
    staleTime: 300_000,
  });
  const off = useMemo(() => (mode === "auto" && cal.data ? new Map(cal.data.days.map((d) => [d.date, d.name])) : undefined), [mode, cal.data]);
  const internoDefault = cal.data?.settings.internoDiasAntes ?? 3;
  const input: CalcInput = {
    expedicao: f.expedicao || undefined, disponibilizacao: f.disponibilizacao || undefined, inicio: f.inicio || undefined,
    dias: Number(f.dias) || 0, tipo: f.tipo, dobro: f.dobro, feriadosExtras: mode === "manual" ? Number(f.feriados) || 0 : 0,
    interno: f.interno === "" ? internoDefault : Number(f.interno) || 0, off,
  };
  const r = calcDeadline(input);
  const ok = !("error" in r);
  const res = r as CalcResult;
  const holidaysIn = ok && mode === "auto" ? res.days.filter((d) => d.n === null && d.reason && !/sábado|domingo/.test(d.reason)) : [];
  const [showDays, setShowDays] = useState(false);

  return (
    <div className="stack gap-12 calc">
      <div className="seg">
        <button className={mode === "manual" ? "on" : ""} onClick={() => setMode("manual")}>Manual</button>
        <button className={mode === "auto" ? "on" : ""} onClick={() => setMode("auto")}>Automática (feriados do sistema)</button>
      </div>
      <FormGrid cols={3}>
        <Field label="Dia da expedição" hint="Intimação pelo portal"><input type="date" className="input" value={f.expedicao} onChange={(e) => up({ expedicao: e.target.value })} /></Field>
        <Field label="Dia da disponibilização" hint="DJEN / diário"><input type="date" className="input" value={f.disponibilizacao} onChange={(e) => up({ disponibilizacao: e.target.value })} /></Field>
        <Field label="Início do prazo" hint={ok && !f.inicio ? `sugerido: ${fmtDate(res.inicio)}` : "deixe vazio para calcular"}><input type="date" className="input" value={f.inicio} onChange={(e) => up({ inicio: e.target.value })} /></Field>
        <Field label="Quantidade de dias"><input type="number" min={1} max={999} className="input" value={f.dias} onChange={(e) => up({ dias: e.target.value })} /></Field>
        <Field label="Contagem">
          <select className="input" value={f.tipo} onChange={(e) => up({ tipo: e.target.value as "uteis" | "corridos" })}><option value="uteis">Dias úteis</option><option value="corridos">Dias corridos</option></select>
        </Field>
        {mode === "manual"
          ? <Field label="Feriados no período" hint="dias de semana sem expediente"><input type="number" min={0} max={60} className="input" value={f.feriados} onChange={(e) => up({ feriados: e.target.value })} /></Field>
          : <Field label="Órgão (feriados locais)" hint={processId ? "vem do processo" : "vazio = só nacionais"}>
              <input className="input" list="calc-organs" value={f.orgao} onChange={(e) => up({ orgao: e.target.value })} placeholder={cal.data?.organName ?? ""} />
              <datalist id="calc-organs">{(cal.data?.organs ?? []).map((o) => <option key={o} value={o} />)}</datalist>
            </Field>}
        <Field label="Prazo interno (dias úteis antes)"><input type="number" min={0} max={30} className="input" value={f.interno} placeholder={String(internoDefault)} onChange={(e) => up({ interno: e.target.value })} /></Field>
        <label className="check" style={{ alignSelf: "end" }}><input type="checkbox" checked={f.dobro} onChange={(e) => up({ dobro: e.target.checked })} /> Prazo em dobro</label>
      </FormGrid>

      {!ok ? <div className="muted small">{(r as { error: string }).error}</div> : (
        <div className="calc-result">
          <div className="calc-main">
            {res.ciencia && <div><span className="muted small">Ciência</span><b>{fmtDate(res.ciencia)}</b></div>}
            {res.publicacao && <div><span className="muted small">Publicação</span><b>{fmtDate(res.publicacao)}</b></div>}
            <div><span className="muted small">Início</span><b>{fmtDate(res.inicio)}</b><span className="muted tiny">{weekday(res.inicio)}</span></div>
            <div className="fatal"><span className="muted small">Prazo fatal</span><b>{fmtDate(res.fatal)}</b><span className="muted tiny">{weekday(res.fatal)}</span></div>
            {res.interno && <div><span className="muted small">Prazo interno</span><b>{fmtDate(res.interno)}</b><span className="muted tiny">{weekday(res.interno)}</span></div>}
          </div>
          {mode === "auto" && cal.isLoading && <div className="muted small">Buscando feriados…</div>}
          {holidaysIn.length > 0 && <div className="small"><b>Sem expediente no período:</b> {holidaysIn.map((d) => `${fmtDate(d.date)} (${d.reason})`).join("; ")}</div>}
          {res.notes.map((n) => <div key={n} className="muted small">• {n}</div>)}
          <button className="link-btn small" onClick={() => setShowDays(!showDays)}>{showDays ? "esconder" : "ver"} a contagem dia a dia</button>
          {showDays && (
            <div className="calc-days">
              {res.days.map((d) => <span key={d.date} className={d.n ? "on" : "off"} title={d.reason ?? ""}>{fmtDate(d.date).slice(0, 5)}<b>{d.n ?? "—"}</b>{!d.n && <i>{d.reason}</i>}</span>)}
            </div>
          )}
          {mode === "manual" && <div className="muted tiny">Manual: conta só fins de semana e os feriados que você informou. A automática usa os feriados, suspensões e o recesso cadastrados no sistema.</div>}
          {onApply && (
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn primary" onClick={() => onApply({ fatalDueAt: res.fatal, internalDueAt: res.interno, note: `Prazo de ${res.totalDias} dia(s) ${f.tipo === "uteis" ? "úteis" : "corridos"} a partir de ${fmtDate(res.inicio)}${mode === "manual" ? " (cálculo manual)" : ""}.` })}>{applyLabel}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const addIso = (iso: string, n: number) => new Date(new Date(`${iso}T00:00:00Z`).getTime() + n * 86_400_000).toISOString().slice(0, 10);

export function DeadlineCalcModal({ onClose, processId, organName, onApply, applyLabel }: { onClose: () => void; processId?: string | null; organName?: string | null; onApply?: (r: CalcApply) => void; applyLabel?: string }): JSX.Element {
  return (
    <Modal title="Calculadora de prazos" onClose={onClose} wide footer={<button className="btn" onClick={onClose}>Fechar</button>}>
      <DeadlineCalculator processId={processId} organName={organName} onApply={onApply ? (r) => { onApply(r); onClose(); } : undefined} applyLabel={applyLabel} />
    </Modal>
  );
}

/** Botão flutuante (acima do chat) em Atividades, Casos e Processos. Dentro de um processo: "Criar atividade com este prazo". */
export function DeadlineFab({ onCreateActivity }: { onCreateActivity: (p: { processId: string | null; caseId: string | null; apply: CalcApply }) => void }): JSX.Element | null {
  const { path } = useLocation();
  const [open, setOpen] = useState<null | { filler: Filler | null }>(null);
  const show = /^\/(atividades|casos|processos)(\/|$)/.test(path);
  if (!show) return null;
  const proc = match("/processos/:id", path)?.id ?? null;
  const kase = match("/casos/:id", path)?.id ?? null;
  const valid = (x: string | null) => (x && /^[0-9a-f-]{36}$/.test(x) ? x : null);
  return (
    <>
      <button className="calc-fab" title="Calculadora de prazos" onClick={() => setOpen({ filler: currentFiller() })}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M8 7h8M8 11h2M12 11h0M14 11h2M8 15h2M12 15h0M14 15h2M8 18h8" /></svg>
      </button>
      {open && <DeadlineCalcModal onClose={() => setOpen(null)} processId={open.filler?.processId ?? valid(proc)}
        applyLabel={open.filler ? "Preencher as datas da atividade" : "Criar atividade com este prazo"}
        onApply={(apply) => (open.filler ? open.filler.fill(apply) : onCreateActivity({ processId: valid(proc), caseId: valid(kase), apply }))} />}
    </>
  );
}
